import { z } from 'zod'

export const organizationSlugSchema = z
	.string()
	.min(3, 'Use 3+ chars, letters/numbers and dashes')
	.regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
		message: 'Use 3+ chars, letters/numbers and dashes',
	})

export const organizationNameSchema = z
	.string()
	.trim()
	.min(1, 'Organization name is required')

export const createOrganizationFormSchema = z.object({
	organizationName: organizationNameSchema,
	organizationSlug: organizationSlugSchema,
	primarySector: z.string().min(1, 'Primary sector is required'),
})

export type CreateOrganizationFormValues = z.infer<
	typeof createOrganizationFormSchema
>

const PERSON_NAME_REGEX = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/

const PERSON_NAME_REGEX_MESSAGE = 'Invalid name'

const personNameCharsOnlySchema = z
	.string()
	.trim()
	.regex(PERSON_NAME_REGEX, { message: PERSON_NAME_REGEX_MESSAGE })

export const userProfileNameSchema = personNameCharsOnlySchema
	.max(50, 'Must be 50 characters or fewer')
	.refine((val) => val.length > 0, { message: 'Cannot be empty' })

const registrationNameSchema = personNameCharsOnlySchema
	.refine((val) => val.length > 0, {
		message: 'Name cannot be empty',
	})
	.refine((val) => val.length >= 3, {
		message: 'Name must be at least 3 characters',
	})

export const userProfileSettingsFormSchema = z.object({
	firstName: userProfileNameSchema,
	lastName: userProfileNameSchema,
	email: z.string().email('Invalid email format'),
	avatar: z.string(),
	phone: z.string(),
	preferences: z.any(),
	notifications: z.any(),
})

export const createUserProfileScheme = z
	.object({
		firstName: registrationNameSchema,
		lastName: registrationNameSchema,
		email: z.string().email('Invalid email format'),
		phone: z.string().min(1, 'Phone number cannot be empty'),
		organizationName: z.string().optional(),
		organizationSlug: z.string().optional(),
		primarySector: z.string().optional(),
		enable_mfa: z.boolean().default(false),
		account_type: z.enum(['personal', 'organization']),
		is_org_owner: z.boolean(),
	})
	.refine(
		(data) => {
			if (data.account_type === 'organization' && data.is_org_owner) {
				return !!data.organizationName
			}
			return true
		},
		{
			message: 'Organization name is required for organization accounts',
			path: ['organizationName'],
		},
	)
	.refine(
		(data) => {
			if (data.account_type === 'organization' && data.is_org_owner) {
				return !!data.organizationSlug
			}
			return true
		},
		{
			message: 'Organization slug is required for organization accounts',
			path: ['organizationSlug'],
		},
	)
	.refine(
		(data) => {
			if (data.account_type === 'organization' && data.is_org_owner) {
				return !!data.primarySector
			}
			return true
		},
		{
			message: 'Primary sector is required for organization accounts',
			path: ['primarySector'],
		},
	)
	.refine(
		(data) => {
			if (
				data.account_type === 'organization' &&
				data.is_org_owner &&
				data.organizationSlug
			) {
				const result = organizationSlugSchema.safeParse(data.organizationSlug)
				return result.success
			}
			return true
		},
		(data) => {
			if (
				data.account_type === 'organization' &&
				data.is_org_owner &&
				data.organizationSlug
			) {
				const result = organizationSlugSchema.safeParse(data.organizationSlug)
				if (!result.success && result.error.errors[0]) {
					return {
						message: result.error.errors[0].message,
						path: ['organizationSlug'],
					}
				}
			}
			return {
				message: 'Invalid organization slug format',
				path: ['organizationSlug'],
			}
		},
	)

export type CreateUserProfileFormScheme = z.infer<
	typeof createUserProfileScheme
>
